// What the signed-in user may open for a customer (ENTRAID-282), from GET
// /me. Display only: the API enforces every rule itself
// (portal/backend/lib/auth.mjs). lib/navigation.js turns it into the
// workbench navigation (toNavClaims).
//
//   - admin: every section of the customer.
//   - qlikCredentials: the customer's Qlik Cloud section only.
//   - developer: the customer's Data reference only.
//   - auditor: the customer's Compliance section only (ENTRAID-307).
//   - viewer: the customer's Overview and Security, read-only (2026-10-25).
//   - reviewer: "Your reviews" in Access reviews only (spec 2026-10-29), while the API honors the role.
// The non-admin roles combine; neither implies admin.

/**
 * True when the signed-in account itself holds onguard-entraid-admin on this
 * customer (ENTRAID-302), from GET /me `access` (the token's active tenant and
 * this app's admin role). It reads the account, never "View as client".
 */
export function holdsAdminHere(me, customerId) {
  const access = me?.access;
  if (typeof customerId !== 'string' || customerId.length === 0) return false;
  if (access?.admin === true && access.customerId === customerId) return true;
  // A user of several customers (ENTRAID-307): the admin role confirmed for that very customer.
  if (membershipOf(me, customerId)?.admin === true) return true;
  // An operator signs in without a tenant: /me lists, for operators only, the
  // customers where the central IdP gives them an active admin membership.
  return Array.isArray(access?.adminCustomerIds) && access.adminCustomerIds.includes(customerId);
}

/**
 * True when an operator account holds no admin role on this customer. Not a
 * Watch rule (that is watchOperatorAccount, which adds the tier): used where
 * the admin role alone decides, such as Compliance and Branding.
 */
export function operatorWithoutAdmin(me, customerId) {
  return me?.user?.isOperator === true && !holdsAdminHere(me, customerId);
}

/**
 * The one UI rule for Watch content: an operator account sees health and
 * classic rules only, unless it holds the admin role on this customer AND the
 * customer's tier is professional or enterprise (ENTRAID-302, Hugo
 * 2026-10-21). /me answers that set as `access.watchCustomerIds`. The same
 * decision as watchAdminForCustomer in the portal (lib/watch-auth.mjs), which
 * is what actually enforces it.
 */
export function watchOperatorAccount(me, customerId) {
  if (me?.user?.isOperator !== true) return false;
  const ids = me?.access?.watchCustomerIds;
  return !(typeof customerId === 'string' && Array.isArray(ids) && ids.includes(customerId));
}

/**
 * True when this viewer reads Watch content (events, cases) of this customer: the customer's own admin, never an
 * operator account (also not in "View as client", which changes only what the UI shows). Display only: the portal
 * enforces the same rule on every Watch content route.
 *
 * @param {object|null} me - the GET /me body
 * @param {{ isOperator?: boolean, admin?: boolean, customerId?: string|null }|null} navClaims - lib/navigation.js claims
 */
export function readsWatchContent(me, navClaims, customerId) {
  if (watchOperatorAccount(me, customerId)) return false;
  const c = navClaims ?? {};
  return holdsAdminHere(me, customerId) || (c.isOperator !== true && c.admin === true && c.customerId === customerId);
}

const NONE = Object.freeze({ customerId: null, admin: false, developer: false, qlikCredentials: false, auditor: false, viewer: false, reviewer: false });

/**
 * The customers a user of several may switch between (ENTRAID-307, Decision 3), from GET /me `access.memberships`:
 * the roles the central IdP confirms for each one. Never for an operator. Display only: every route checks the
 * membership of the customer in its URL itself. A membership marked `stale` (its customer was deleted, ENTRAID-350) is
 * never offered: it is not a usable tenant, so neither the switcher nor the landing ever picks it.
 *
 * @returns {Array<{ customerId: string, companyName: string, admin: boolean, developer: boolean, qlikCredentials: boolean, auditor: boolean, viewer: boolean }>}
 */
export function memberships(me) {
  if (me?.user?.isOperator === true) return [];
  const list = me?.access?.memberships;
  return Array.isArray(list) ? list.filter((m) => typeof m?.customerId === 'string' && m.customerId.length > 0 && m.stale !== true) : [];
}

/** The membership of this customer, or null. */
export function membershipOf(me, customerId) {
  return memberships(me).find((m) => m.customerId === customerId) ?? null;
}

const fromMembership = (m) => ({
  customerId: m.customerId,
  admin: m.admin === true,
  developer: m.developer === true,
  qlikCredentials: m.qlikCredentials === true,
  auditor: m.auditor === true,
  viewer: m.viewer === true,
  reviewer: m.reviewer === true,
});

/**
 * @param {object|null} me - the GET /me body
 * @param {{ customerId: string }|null} impersonating - an operator's "View as client"
 * @param {string|null} [scopeCustomerId] - the customer the current page is about (from its path), if any
 */
export function effectiveAccess(me, impersonating, scopeCustomerId = null) {
  // An operator viewing as a client sees what the client's admin sees. Only
  // an operator can: for anyone else the flag is ignored (UX defense in depth;
  // the API enforces the operator role on every route regardless).
  if (impersonating?.customerId && me?.user?.isOperator === true) {
    return { customerId: impersonating.customerId, admin: true, developer: false, qlikCredentials: false, auditor: false, viewer: false, reviewer: false };
  }
  const access = me?.access ?? {};
  // A user of several customers (ENTRAID-307): on a page about one of them, its own confirmed roles; elsewhere, the
  // token's active tenant when there is one, else the first membership (the central IdP sets no active tenant then).
  const several = memberships(me);
  if (several.length > 0) {
    const here = typeof scopeCustomerId === 'string' ? several.find((m) => m.customerId === scopeCustomerId) : null;
    if (here) return fromMembership(here);
    const active = typeof access.customerId === 'string' && access.customerId.length > 0 ? access.customerId : null;
    if (!active) return fromMembership(several[0]);
  }
  // Only a customer admin gets their customer record on /me.
  const adminCustomerId = me?.customer?.customerId ?? null;
  if (adminCustomerId) {
    return { customerId: adminCustomerId, admin: true, developer: access.developer === true, qlikCredentials: access.qlikCredentials === true, auditor: access.auditor === true, viewer: access.viewer === true, reviewer: access.reviewer === true };
  }
  if (typeof access.customerId !== 'string' || access.customerId.length === 0) return NONE;
  return {
    customerId: access.customerId,
    admin: false,
    developer: access.developer === true,
    qlikCredentials: access.qlikCredentials === true,
    auditor: access.auditor === true,
    viewer: access.viewer === true,
    reviewer: access.reviewer === true,
  };
}
